/**
 * Keyboard Navigation Hook
 * Provides keyboard shortcuts for the parsing review workstation
 */

import { useEffect, useCallback, useState } from 'react';

/**
 * Default keyboard shortcut mappings
 */
const DEFAULT_SHORTCUTS = {
  // Navigation
  nextItem: ['j', 'ArrowDown'],
  prevItem: ['k', 'ArrowUp'],
  firstItem: ['g g'],  // Vim-style double key
  lastItem: ['g l'],

  // Actions
  assign: ['a'],
  approve: ['ctrl+s', 'meta+s'],
  approveAndNext: ['ctrl+shift+s', 'meta+shift+s'],
  reject: ['ctrl+r', 'meta+r'],
  skip: ['s'],

  // Editing
  parseInput: ['ctrl+Enter', 'meta+Enter'],
  focusSmartInput: ['i'],
  escapeEdit: ['Escape'],

  // Document view
  zoomIn: ['=', '+'],
  zoomOut: ['-', '_'],
  toggleView: ['v'],
  searchDocument: ['ctrl+f', 'meta+f'],

  // Type selection
  typeMenu: ['t'],
  typeFlight: ['t f'],
  typeHotel: ['t h'],
  typeCarRental: ['t c'],
  typePrivateFlight: ['t p'],

  // Queue
  toggleQueue: ['q'],

  // Help
  showHelp: ['?', 'shift+/']
};

/**
 * Parse a shortcut string into its components
 */
const parseShortcut = (shortcut) => {
  const parts = shortcut.toLowerCase().split('+');
  return {
    ctrl: parts.includes('ctrl'),
    meta: parts.includes('meta'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
    key: parts[parts.length - 1]
  };
};

/**
 * Check if a keyboard event matches a shortcut
 */
const matchesShortcut = (event, shortcut) => {
  const parsed = parseShortcut(shortcut);

  // Handle special case for space-separated sequences (like "g g")
  if (shortcut.includes(' ')) {
    return false; // Sequence shortcuts handled separately
  }

  const keyMatches =
    event.key.toLowerCase() === parsed.key ||
    event.code.toLowerCase() === parsed.key ||
    event.code.toLowerCase() === `key${parsed.key}`;

  return (
    keyMatches &&
    event.ctrlKey === parsed.ctrl &&
    event.metaKey === parsed.meta &&
    event.shiftKey === parsed.shift &&
    event.altKey === parsed.alt
  );
};

/**
 * Hook for keyboard navigation
 */
export const useKeyboardNav = ({
  onNextItem,
  onPrevItem,
  onFirstItem,
  onLastItem,
  onAssign,
  onApprove,
  onApproveAndNext,
  onReject,
  onSkip,
  onParseInput,
  onFocusSmartInput,
  onEscapeEdit,
  onZoomIn,
  onZoomOut,
  onToggleView,
  onSearchDocument,
  onTypeChange,
  onToggleQueue,
  onShowHelp,
  enabled = true,
  shortcuts = DEFAULT_SHORTCUTS
} = {}) => {
  const [keySequence, setKeySequence] = useState([]);
  const [sequenceTimeout, setSequenceTimeout] = useState(null);

  // Clear key sequence after timeout
  const clearSequence = useCallback(() => {
    setKeySequence([]);
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Don't capture when typing in inputs (unless it's a modifier combo)
    const target = event.target;
    const isInput = target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable;

    const hasModifier = event.ctrlKey || event.metaKey;

    // Allow some shortcuts even in inputs
    const allowedInInputs = ['Escape', 'ctrl+Enter', 'meta+Enter', 'ctrl+s', 'meta+s'];
    const currentKey = `${event.ctrlKey ? 'ctrl+' : ''}${event.metaKey ? 'meta+' : ''}${event.shiftKey ? 'shift+' : ''}${event.key}`;

    if (isInput && !allowedInInputs.some(s => currentKey.toLowerCase().includes(s.toLowerCase().replace('+', '')))) {
      // Clear sequence if typing in input
      if (keySequence.length > 0) {
        clearSequence();
      }
      return;
    }

    // Build current sequence
    const newSequence = [...keySequence, event.key.toLowerCase()];
    const sequenceStr = newSequence.join(' ');

    // Clear previous timeout
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }

    // Check for sequence matches
    let matched = false;

    // Check type shortcuts (t + key)
    if (sequenceStr === 't f' && onTypeChange) {
      onTypeChange('flight');
      matched = true;
    } else if (sequenceStr === 't h' && onTypeChange) {
      onTypeChange('hotel');
      matched = true;
    } else if (sequenceStr === 't c' && onTypeChange) {
      onTypeChange('car_rental');
      matched = true;
    } else if (sequenceStr === 't p' && onTypeChange) {
      onTypeChange('private_flight');
      matched = true;
    } else if (sequenceStr === 'g g' && onFirstItem) {
      onFirstItem();
      matched = true;
    } else if (sequenceStr === 'g l' && onLastItem) {
      onLastItem();
      matched = true;
    }

    if (matched) {
      event.preventDefault();
      clearSequence();
      return;
    }

    // Check for single-key matches
    for (const [action, keys] of Object.entries(shortcuts)) {
      for (const shortcut of keys) {
        if (shortcut.includes(' ')) continue; // Skip sequence shortcuts

        if (matchesShortcut(event, shortcut)) {
          event.preventDefault();
          clearSequence();

          switch (action) {
            case 'nextItem':
              onNextItem?.();
              break;
            case 'prevItem':
              onPrevItem?.();
              break;
            case 'assign':
              onAssign?.();
              break;
            case 'approve':
              onApprove?.();
              break;
            case 'approveAndNext':
              onApproveAndNext?.();
              break;
            case 'reject':
              onReject?.();
              break;
            case 'skip':
              onSkip?.();
              break;
            case 'parseInput':
              onParseInput?.();
              break;
            case 'focusSmartInput':
              onFocusSmartInput?.();
              break;
            case 'escapeEdit':
              onEscapeEdit?.();
              break;
            case 'zoomIn':
              onZoomIn?.();
              break;
            case 'zoomOut':
              onZoomOut?.();
              break;
            case 'toggleView':
              onToggleView?.();
              break;
            case 'searchDocument':
              onSearchDocument?.();
              break;
            case 'toggleQueue':
              onToggleQueue?.();
              break;
            case 'typeMenu':
              // Just track sequence start
              setKeySequence(['t']);
              setSequenceTimeout(setTimeout(clearSequence, 1000));
              return;
            case 'showHelp':
              onShowHelp?.();
              break;
            default:
              break;
          }
          return;
        }
      }
    }

    // Update sequence for potential multi-key shortcuts
    if (event.key.length === 1 && !hasModifier) {
      setKeySequence(newSequence);
      setSequenceTimeout(setTimeout(clearSequence, 1000));
    }
  }, [
    enabled, keySequence, sequenceTimeout, clearSequence, shortcuts,
    onNextItem, onPrevItem, onFirstItem, onLastItem, onAssign, onApprove,
    onApproveAndNext, onReject, onSkip, onParseInput, onFocusSmartInput,
    onEscapeEdit, onZoomIn, onZoomOut, onToggleView, onSearchDocument,
    onTypeChange, onToggleQueue, onShowHelp
  ]);

  // Attach/detach event listener
  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [enabled, handleKeyDown]);

  return {
    keySequence,
    clearSequence
  };
};

/**
 * Keyboard shortcuts help data
 */
export const KEYBOARD_HELP = [
  {
    category: 'Navigation',
    shortcuts: [
      { keys: ['J', '↓'], description: 'Next item' },
      { keys: ['K', '↑'], description: 'Previous item' },
      { keys: ['G G'], description: 'First item' },
      { keys: ['G L'], description: 'Last item' },
      { keys: ['Q'], description: 'Toggle queue drawer' }
    ]
  },
  {
    category: 'Actions',
    shortcuts: [
      { keys: ['A'], description: 'Assign to me' },
      { keys: ['⌘S', 'Ctrl+S'], description: 'Approve' },
      { keys: ['⌘⇧S', 'Ctrl+Shift+S'], description: 'Approve & next' },
      { keys: ['⌘R', 'Ctrl+R'], description: 'Reject' },
      { keys: ['S'], description: 'Skip to next' }
    ]
  },
  {
    category: 'Editing',
    shortcuts: [
      { keys: ['I'], description: 'Focus smart input' },
      { keys: ['⌘↵', 'Ctrl+Enter'], description: 'Parse input' },
      { keys: ['Esc'], description: 'Cancel edit' }
    ]
  },
  {
    category: 'Document',
    shortcuts: [
      { keys: ['+', '='], description: 'Zoom in' },
      { keys: ['-'], description: 'Zoom out' },
      { keys: ['V'], description: 'Toggle view mode' },
      { keys: ['⌘F', 'Ctrl+F'], description: 'Search in document' }
    ]
  },
  {
    category: 'Type Selection',
    shortcuts: [
      { keys: ['T F'], description: 'Set to Flight' },
      { keys: ['T H'], description: 'Set to Hotel' },
      { keys: ['T C'], description: 'Set to Car Rental' },
      { keys: ['T P'], description: 'Set to Private Flight' }
    ]
  },
  {
    category: 'Help',
    shortcuts: [
      { keys: ['?'], description: 'Show this help' }
    ]
  }
];

export default useKeyboardNav;
